import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface FaqItem {
  id: string;
  question: string;
  answer: string;
  category: string;
  published: boolean;
}

export interface FaqSettings {
  pageTitle: string;
  pageSubtitle: string;
  categories: string[];
  faqs: FaqItem[];
}

export const defaultFaqSettings: FaqSettings = {
  pageTitle: 'Frequently Asked Questions',
  pageSubtitle: 'Everything you need to know about Stingray marine batteries — warranties, shipping, dealer programs and technical specs.',
  categories: ['General', 'Warranty', 'Shipping', 'Technical', 'Dealer'],
  faqs: [
    {
      id: 'faq-1',
      question: 'How do I become a Stingray authorized dealer?',
      answer: 'Apply via the Become a Dealer page on our website or email us at info@stingrayenergy.net. Our team reviews applications within 3–5 business days and will contact you with next steps.',
      category: 'Dealer',
      published: true,
    },
    {
      id: 'faq-2',
      question: 'What is the warranty on LiFePO4 lithium batteries?',
      answer: 'All Stingray LiFePO4 lithium batteries come with a 5-year limited warranty covering manufacturing defects and premature capacity loss. The warranty is non-transferable and applies to the original purchaser.',
      category: 'Warranty',
      published: true,
    },
    {
      id: 'faq-3',
      question: 'What is the warranty on AGM batteries?',
      answer: 'Stingray AGM series batteries carry a 3-year limited warranty. This covers defects in materials and workmanship under normal use conditions.',
      category: 'Warranty',
      published: true,
    },
    {
      id: 'faq-4',
      question: 'What is the warranty on Gel batteries?',
      answer: 'Stingray Gel series batteries come with a 2-year limited warranty covering manufacturing defects under normal marine use conditions.',
      category: 'Warranty',
      published: true,
    },
    {
      id: 'faq-5',
      question: 'Does Stingray ship internationally?',
      answer: 'Yes. Stingray batteries are distributed in 50+ countries worldwide through our authorized dealer network. Contact us and we will help connect you with an authorized dealer in your region.',
      category: 'Shipping',
      published: true,
    },
    {
      id: 'faq-6',
      question: 'How long does shipping take?',
      answer: 'Domestic US orders typically ship within 2–3 business days. International orders vary by region and dealer — contact your local authorized dealer for estimated delivery times.',
      category: 'Shipping',
      published: true,
    },
    {
      id: 'faq-7',
      question: 'Can I use a LiFePO4 battery as a drop-in replacement for AGM?',
      answer: 'In most cases, yes. LiFePO4 batteries are compatible with standard 12V charging systems. However, for optimal performance and longevity, we recommend using a lithium-compatible charger. Check your vessel\'s charging system voltage before switching.',
      category: 'Technical',
      published: true,
    },
    {
      id: 'faq-8',
      question: 'What is the cycle life of Stingray LiFePO4 batteries?',
      answer: 'Stingray LiFePO4 batteries are rated for 2,000+ charge cycles at 80% depth of discharge, compared to 300–500 cycles for AGM. This translates to a 10-year design life under typical marine use.',
      category: 'Technical',
      published: true,
    },
    {
      id: 'faq-9',
      question: 'Are Stingray batteries waterproof?',
      answer: 'Our LiFePO4 series carries an IP67 waterproof rating, meaning they can withstand temporary submersion up to 1 meter for 30 minutes. AGM and Gel batteries are sealed and spill-proof but not rated for submersion.',
      category: 'Technical',
      published: true,
    },
    {
      id: 'faq-10',
      question: 'How do I file a warranty claim?',
      answer: 'Visit our Warranty Policy page and use the online Warranty Claim Form. You\'ll need your proof of purchase, battery serial number, and a description of the issue. Our team responds within 2 business days.',
      category: 'Warranty',
      published: true,
    },
    {
      id: 'faq-11',
      question: 'Can I connect multiple batteries in series or parallel?',
      answer: 'Yes. Stingray LiFePO4 batteries support both series (for higher voltage) and parallel (for higher capacity) configurations. Always use batteries of the same model and age when connecting in parallel. Contact our technical team for system design guidance.',
      category: 'Technical',
      published: true,
    },
    {
      id: 'faq-12',
      question: 'What payment methods do dealers accept?',
      answer: 'Payment methods vary by authorized dealer. Most accept major credit cards, bank transfers, and dealer credit accounts. Contact your regional dealer for specific payment options.',
      category: 'General',
      published: true,
    },
  ],
};

const STORAGE_KEY = 'stingray_faq_settings';

interface FaqContextValue {
  settings: FaqSettings;
  updateSettings: (s: FaqSettings) => void;
  resetSettings: () => void;
}

const FaqContext = createContext<FaqContextValue>({
  settings: defaultFaqSettings,
  updateSettings: () => {},
  resetSettings: () => {},
});

export function FaqProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<FaqSettings>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) {
        return defaultFaqSettings;
      }

      const parsed = { ...defaultFaqSettings, ...JSON.parse(saved) } as FaqSettings;
      return {
        ...parsed,
        faqs: parsed.faqs.map((faq) => ({
          ...faq,
          answer: faq.answer
            .replaceAll('info@cooltechmarine.com', 'info@stingrayenergy.net')
            .replaceAll('support@stingray-marine.com', 'info@stingrayenergy.net')
            .replaceAll('privacy@stingray-marine.com', 'info@stingrayenergy.net'),
        })),
      };
    } catch {
      return defaultFaqSettings;
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  const updateSettings = (s: FaqSettings) => setSettings(s);
  const resetSettings = () => {
    setSettings(defaultFaqSettings);
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <FaqContext.Provider value={{ settings, updateSettings, resetSettings }}>
      {children}
    </FaqContext.Provider>
  );
}

export const useFaq = () => useContext(FaqContext);
